import React, { useEffect, useState } from "react";
import { CreditCard, Plus, Save, Power, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const EMPTY = { plan_id: "", name: "", price_rupees: "", token_qty: "", features: "", active: true };

export default function Plans() {
  const [d, setD] = useState(null);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', form}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/plans").then((r) => setD(r.data)).catch(() => toast.error("Could not load plans"));
  useEffect(() => { load(); }, []);

  const openCreate = () => setModal({ mode: "create", form: { ...EMPTY } });
  const openEdit = (p) => setModal({ mode: "edit", form: {
    plan_id: p.plan_id, name: p.name, price_rupees: p.base_price_paise / 100,
    token_qty: p.token_qty, features: (p.features || []).join("\n"), active: p.active } });

  const save = async () => {
    const f = modal.form;
    const payload = {
      name: f.name, base_price_paise: Math.round(Number(f.price_rupees) * 100),
      token_qty: Number(f.token_qty), active: f.active,
      features: f.features.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    setBusy(true);
    try {
      if (modal.mode === "create") {
        if (!f.plan_id.trim()) { toast.error("Plan ID required"); setBusy(false); return; }
        await api.post("/admin/plans", { plan_id: f.plan_id.trim(), ...payload });
        toast.success("Plan created");
      } else {
        await api.put(`/admin/plans/${f.plan_id}`, payload);
        toast.success("Plan updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const toggle = async (p) => {
    try { await api.put(`/admin/plans/${p.plan_id}`, { active: !p.active }); toast.success(p.active ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-plans">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-emerald-400" /><h1 className="font-heading text-2xl font-bold">Subscription plans</h1></div>
        <button data-testid="plan-create-btn" onClick={openCreate} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400"><Plus className="h-4 w-4" /> New plan</button>
      </div>
      <p className="text-xs text-slate-500">Prices are GST-inclusive (gross). GST @ {(d.gst_bps / 100).toFixed(0)}% is extracted; net funds the central campaign budget. Plans with history are soft-deactivated, never deleted.</p>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {d.plans.map((p) => (
          <div key={p.plan_id} data-testid={`plan-card-${p.plan_id}`} className={`ledger-card rounded-2xl p-5 ${!p.active ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-lg font-bold">{p.name}</p>
                <p className="font-mono text-xs text-slate-500">{p.plan_id}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${p.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-300"}`}>{p.active ? "ACTIVE" : "INACTIVE"}</span>
            </div>
            <p className="mt-3 font-heading text-3xl font-extrabold text-emerald-400">{p.gross_display}</p>
            <div className="mt-2 space-y-1 text-xs text-slate-400">
              <div className="flex justify-between"><span>Net (funds budget)</span><span className="font-mono text-slate-200">{p.net_display}</span></div>
              <div className="flex justify-between"><span>GST</span><span className="font-mono">{p.gst_display}</span></div>
              <div className="flex justify-between"><span>Tokens</span><span className="font-mono text-slate-200">{p.token_qty.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span>Subscriptions</span><span className="font-mono">{p.subscription_count}</span></div>
            </div>
            <div className="mt-4 flex gap-2">
              <button data-testid={`plan-edit-${p.plan_id}`} onClick={() => openEdit(p)} className="flex-1 rounded-full border border-slate-700 py-1.5 text-xs hover:border-emerald-500/50">Edit</button>
              <button data-testid={`plan-toggle-${p.plan_id}`} onClick={() => toggle(p)} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs ${p.active ? "border-amber-500/40 text-amber-300" : "border-emerald-500/40 text-emerald-300"}`}><Power className="h-3 w-3" />{p.active ? "Off" : "On"}</button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="plan-modal">
          <div className="absolute inset-0 bg-slate-950/70" onClick={() => setModal(null)} />
          <div className="relative w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-heading text-lg font-bold">{modal.mode === "create" ? "New plan" : "Edit plan"}</p>
              <button onClick={() => setModal(null)} aria-label="Close" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              {modal.mode === "create" && <Inp label="Plan ID (slug)" testid="plan-field-id" value={modal.form.plan_id} onChange={(v) => setModal({ ...modal, form: { ...modal.form, plan_id: v } })} />}
              <Inp label="Name" testid="plan-field-name" value={modal.form.name} onChange={(v) => setModal({ ...modal, form: { ...modal.form, name: v } })} />
              <Inp label="Price ₹ (gross, GST-incl)" type="number" testid="plan-field-price" value={modal.form.price_rupees} onChange={(v) => setModal({ ...modal, form: { ...modal.form, price_rupees: v } })} />
              <Inp label="Token quantity" type="number" testid="plan-field-tokens" value={modal.form.token_qty} onChange={(v) => setModal({ ...modal, form: { ...modal.form, token_qty: v } })} />
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Features (one per line)</label>
                <textarea data-testid="plan-field-features" rows={3} value={modal.form.features} onChange={(e) => setModal({ ...modal, form: { ...modal.form, features: e.target.value } })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm outline-none focus:border-emerald-500" />
              </div>
              <label className="flex items-center gap-2 text-sm"><input data-testid="plan-field-active" type="checkbox" checked={modal.form.active} onChange={(e) => setModal({ ...modal, form: { ...modal.form, active: e.target.checked } })} /> Active</label>
            </div>
            <button data-testid="plan-save-btn" onClick={save} disabled={busy} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-2.5 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-widest text-slate-500">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm outline-none focus:border-emerald-500" />
    </div>
  );
}
